// StaffAttendanceDashboard — today's staff/student attendance at a glance.
// Colors follow the dataviz method: status colors (good/critical/muted) for
// present/absent/unmarked state, one sequential hue (blue) for the monthly
// per-staff magnitude comparison. Every color-coded value also ships a
// visible label/legend — never color alone.
import { useEffect, useState } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import {
  getStaffAttendanceDashboard, getStaffAttendanceToday,
  getStaffAttendanceSummary, getStaff, getStudentsAttendanceSummary,
} from '../api/client';
import { Card, CardHeader, KpiTile } from '../ui/Card';

// Status palette (fixed, never themed) — from the dataviz reference palette.
const STATUS = {
  good:     '#0ca30c',
  critical: '#d03b3b',
  muted:    '#898781',
};
const SEQUENTIAL_BLUE = '#2a78d6';

const monthNow = () => new Date().toISOString().slice(0, 7); // 'YYYY-MM'

export default function StaffAttendanceDashboard() {
  const [dash,      setDash]      = useState(null);
  const [today,     setToday]     = useState([]);
  const [monthly,   setMonthly]   = useState([]);
  const [pie,       setPie]       = useState(null);
  const [month,     setMonth]     = useState(monthNow());
  const [msg,       setMsg]       = useState('');

  useEffect(() => { load(); }, [month]);

  async function load() {
    try {
      const [d, t, students, staffList] = await Promise.all([
        getStaffAttendanceDashboard(),
        getStaffAttendanceToday(),
        getStudentsAttendanceSummary(),
        getStaff(),
      ]);
      setDash(d);
      setToday(t);
      setPie(students);

      const summaries = await Promise.all(
        staffList.map(s => getStaffAttendanceSummary(s.id, month).then(r => ({ name: s.name, days_present: r.days_present })))
      );
      setMonthly(summaries);
    } catch (err) {
      setMsg('Failed to load dashboard: ' + (err.response?.data?.error || err.message));
    }
  }

  if (msg) return <p className="text-critical font-bold">{msg}</p>;
  if (!dash || !pie) return <p className="text-ink-soft">Loading…</p>;

  const notYetMarked = Math.max(0, pie.total_students - pie.present_today - pie.absent_today);
  const pieData = [
    { name: 'Present',       value: pie.present_today, color: STATUS.good },
    { name: 'Absent',        value: pie.absent_today,  color: STATUS.critical },
    { name: 'Not yet marked', value: notYetMarked,      color: STATUS.muted },
  ];

  const monthInputCls = "border border-line rounded-s px-2 py-1 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit";

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Staff & Student Attendance</h2>

      {/* Stat tiles — single current values, not charts */}
      <div className="flex gap-4 mb-6">
        <KpiTile label="Staff Present Today" value={`${dash.present_today} / ${dash.total_staff}`} />
        <KpiTile
          label="Out of Range Today"
          value={<span style={{ color: dash.out_of_range_today > 0 ? STATUS.critical : STATUS.good }}>{dash.out_of_range_today}</span>}
        />
      </div>

      {/* Who's present today — status color is always paired with an icon + text label */}
      <Card className="mb-8">
        <CardHeader title="Staff — Today" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                {['Name', 'Designation', 'Checked In', 'Status'].map(h => <th key={h} className="px-4 py-2.5">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {today.map(s => (
                <tr key={s.staff_id} className="border-t border-line-soft hover:bg-transit-tint/40">
                  <td className="px-4 py-3 font-semibold text-ink">{s.name}</td>
                  <td className="px-4 py-3">{s.designation}</td>
                  <td className="px-4 py-3">{s.checked_in_at ? new Date(s.checked_in_at).toLocaleTimeString() : '—'}</td>
                  <td className="px-4 py-3">
                    {!s.checked_in_at
                      ? <span style={{ color: STATUS.muted }}>⏺ Not checked in</span>
                      : s.out_of_range
                        ? <span style={{ color: STATUS.critical }}>⚠ Out of range ({s.distance_meters}m)</span>
                        : <span style={{ color: STATUS.good }}>✓ Present</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Students today — 3-slice donut (present / absent / not yet marked) */}
      <Card className="mb-8">
        <CardHeader title="Students — Today" />
        <div className="flex gap-6 items-center flex-wrap p-5">
          <ResponsiveContainer width={280} height={220}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}
                paddingAngle={2} stroke="#fcfcfb" strokeWidth={2}>
                {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          {/* Accessible table view of the same data — never color-alone */}
          <table className="w-full text-sm max-w-[280px]">
            <tbody>
              {pieData.map(row => (
                <tr key={row.name}>
                  <td className="px-4 py-3"><span style={{ color: row.color }}>●</span> {row.name}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{row.value}</td>
                </tr>
              ))}
              <tr>
                <td className="px-4 py-3"><strong>Total</strong></td>
                <td className="px-4 py-3 text-right tabular-nums"><strong>{pie.total_students}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Monthly per-staff days present — magnitude comparison, sequential hue */}
      <Card>
        <CardHeader title="Monthly Attendance" action={
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} className={monthInputCls} />
        } />
        <div className="p-5">
          <ResponsiveContainer width="100%" height={Math.max(160, monthly.length * 36)}>
            <BarChart data={monthly} layout="vertical" margin={{ left: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" horizontal={false} />
              <XAxis type="number" allowDecimals={false} stroke="#898781" />
              <YAxis type="category" dataKey="name" width={120} stroke="#898781" />
              <Tooltip />
              <Bar dataKey="days_present" name="Days Present" fill={SEQUENTIAL_BLUE} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
